export const AI_EXPERIENCE_PRESETS = [
  {
    id: "concise_pro",
    label: "Concise & professional (default)",
    value:
      "Write a concise paragraph from a high-school counselor describing specific experiences working with the student (academic planning, leadership, service). Keep it professional and concrete.",
  },
  {
    id: "intellectual_curiosity",
    label: "Intellectual Curiosity & Drive",
    value:
      "Draft a paragraph emphasizing the student's intellectual curiosity, passion for learning beyond the classroom, and their ability to pursue advanced or interdisciplinary studies.",
  },
  {
    id: "grit_resilience",
    label: "Grit & Resilience",
    value:
      "Draft a paragraph focusing on the student's ability to overcome personal or academic challenges, demonstrating maturity, perseverance, and a growth mindset.",
  },
  {
    id: "leadership_impact",
    label: "Leadership & Community Impact",
    value:
      "Draft a paragraph highlighting the student's leadership roles, their initiative in creating positive change, and the measurable impact they have had on their school or local community.",
  },
  {
    id: "diversity_perspective",
    label: "Unique Perspective & Background",
    value:
      "Draft a paragraph describing how the student's unique personal background, cultural experiences, or non-traditional journey will contribute to the diversity and intellectual life of the college campus.",
  },
  {
    id: "stem_innovation",
    label: "STEM & Innovation",
    value:
      "Draft a paragraph emphasizing the student's hands-on experience in STEM, their aptitude for research and problem-solving, and their potential to innovate or contribute to new knowledge.",
  },
  {
    id: "creativity_arts",
    label: "Creativity & The Arts",
    value:
      "Draft a paragraph highlighting the student's creativity, artistic vision, and ability to use their chosen medium to express ideas, solve problems, or impact others.",
  },
  {
    id: "communication_skills",
    label: "Communication & Collaboration",
    value:
      "Draft a paragraph focusing on the student's exceptional communication skills—both written and verbal—and their proven ability to collaborate effectively in diverse group settings.",
  },
  {
    id: "global_citizenship",
    label: "Global Citizenship & Awareness",
    value:
      "Draft a paragraph centered on the student's global awareness, engagement with international issues, and their commitment to making a positive impact on a global scale.",
  },
  {
    id: "entrepreneurial",
    label: "Entrepreneurial Mindset",
    value:
      "Draft a paragraph showcasing the student’s entrepreneurial spirit, including any ventures they have started, their ability to identify opportunities, and their resourcefulness.",
  },
  {
    id: "technical_proficiencies",
    label: "Technical Proficiencies & Application",
    value:
      "Draft a paragraph detailing the student's advanced technical skills (e.g., coding languages, software) and how they have applied these proficiencies to create projects or solve real-world problems.",
  },
  {
    id: "academic_potential",
    label: "Academic Potential & Readiness",
    value:
      "Draft a paragraph highlighting the student’s academic discipline, ability to handle a rigorous course load, and readiness for college-level coursework.",
  },
  {
    id: "purposeful_pathway",
    label: "Purposeful Pathway & Fit",
    value:
      "Draft a paragraph explaining why the student is a strong fit for their chosen major or career path, demonstrating a clear, well-thought-out purpose for their studies.",
  },

  /* ------------------------ New, counselor-friendly presets ------------------------ */
  {
    id: "character_integrity",
    label: "Character & Integrity",
    value:
      "Draft a paragraph highlighting the student's integrity, reliability, and ethical judgment. Include 1–2 specific examples (e.g., honoring commitments, resolving conflicts fairly, modeling honesty) and note the trust they’ve earned from adults and peers.",
  },
  {
    id: "service_learning",
    label: "Service & Civic Engagement",
    value:
      "Draft a paragraph focusing on the student’s commitment to service (e.g., tutoring, volunteering, organizing drives). Quantify impact where possible (hours, funds raised, people served) and explain how these efforts shaped their values.",
  },
  {
    id: "first_gen_trailblazer",
    label: "First-Gen / Trailblazer",
    value:
      "Draft a paragraph that respectfully highlights the student’s role as a first-generation college applicant or academic trailblazer in their family, including how they navigate systems, seek resources, and support siblings/peers without reducing them to a narrative of hardship.",
  },
  {
    id: "scholar_athlete_balance",
    label: "Scholar-Athlete Balance",
    value:
      "Draft a paragraph showing how the student balances rigorous academics with athletics. Include time management, leadership on the team, and a concrete example of transferring athletic discipline to academic success.",
  },
  {
    id: "research_inquiry",
    label: "Research & Inquiry Skills",
    value:
      "Draft a paragraph describing the student’s aptitude for research: framing questions, designing methods, interpreting data, and communicating findings. Mention any fairs, posters, papers, or mentorships.",
  },
  {
    id: "humanities_analysis",
    label: "Humanities: Analysis & Voice",
    value:
      "Draft a paragraph highlighting the student’s analytical reading and original writing in humanities or social sciences. Reference a paper, seminar contribution, or project that showcases nuanced argumentation and evidence.",
  },
  {
    id: "design_thinking_maker",
    label: "Design Thinking & Maker Mindset",
    value:
      "Draft a paragraph emphasizing the student’s iterative problem-solving, prototyping, and user-focused design. Include a project where they identified a real need and built a practical solution.",
  },
  {
    id: "civic_leadership_policy",
    label: "Civic Leadership & Policy",
    value:
      "Draft a paragraph describing the student’s engagement with governance or policy (student government, advocacy, debate). Note outcomes such as proposals passed, events organized, or policies improved.",
  },
  {
    id: "empathy_peer_support",
    label: "Empathy & Peer Support",
    value:
      "Draft a paragraph focusing on the student’s empathy, listening skills, and peer mentorship. Include a concrete moment where they supported a classmate or built inclusive community.",
  },
  {
    id: "initiative_self_directed",
    label: "Initiative & Self-Directed Learning",
    value:
      "Draft a paragraph showing the student’s initiative in pursuing independent learning (e.g., online coursework, personal projects, internships). Explain how they set goals, sustained effort, and shared results.",
  },
  {
    id: "teamwork_cross_functional",
    label: "Teamwork Across Differences",
    value:
      "Draft a paragraph illustrating the student’s ability to collaborate with diverse peers, mediate disagreements, and elevate group work. Include a specific outcome that depended on their teamwork.",
  },
  {
    id: "work_ethic_time_management",
    label: "Work Ethic & Time Management",
    value:
      "Draft a paragraph demonstrating the student’s reliability, organization, and follow-through amid multiple commitments (AP/IB, clubs, job, family responsibilities). Cite concrete practices and results.",
  },
  {
    id: "mentorship_peer_leadership",
    label: "Mentorship & Peer Leadership",
    value:
      "Draft a paragraph highlighting the student’s role mentoring younger students or leading clubs/teams. Include how they plan meetings, develop others’ skills, and ensure continuity after they graduate.",
  },
  {
    id: "steam_integration",
    label: "STEAM Integration",
    value:
      "Draft a paragraph showing how the student bridges STEM with the arts (e.g., data visualization, music tech, architecture, digital media). Provide a project that demonstrates both rigor and creativity.",
  },
  {
    id: "bilingual_cross_cultural",
    label: "Bilingual & Cross-Cultural Strengths",
    value:
      "Draft a paragraph describing the student’s bilingual/multilingual abilities and cross-cultural fluency. Include a moment when these skills advanced collaboration, access, or community building.",
  },
  {
    id: "impact_metrics",
    label: "Measured Impact & Outcomes",
    value:
      "Draft a paragraph that foregrounds measurable outcomes of the student’s initiatives (participation growth, funds raised, competition results, sustainability metrics). Keep tone evidence-based and specific.",
  },
  {
    id: "honors_awards",
    label: "Honors, Awards & Distinctions",
    value:
      "Draft a paragraph contextualizing the student’s awards or honors (selectivity, criteria, scope). Explain what the recognition says about their potential and how they leveraged it for further learning.",
  },
  {
    id: "capstone_internship",
    label: "Capstone / Internship Readiness",
    value:
      "Draft a paragraph describing the student’s performance in a capstone, internship, or dual-enrollment course, emphasizing professionalism, applied learning, and supervisor feedback.",
  },
  {
    id: "composite_overview",
    label: "Composite Counselor Overview",
    value:
      "Draft a paragraph that synthesizes 3–4 of the student’s strongest themes (e.g., curiosity, leadership, service, resilience) with brief evidence for each, ending with a clear, confident endorsement.",
  },
  {
    id: "closing_endorsement",
    label: "Strong Closing Endorsement",
    value:
      "Draft a final, succinct endorsement paragraph from a counselor affirming the student’s readiness for college, campus contribution, and character, with a warm but professional tone.",
  },
];
